import { useState, useEffect } from "react";

/** Breakpoint thresholds matching Tailwind's md/lg defaults */
const MOBILE_MAX = 767; // 0..767 → mobile
const TABLET_MAX = 1023; // 768..1023 → tablet
// 1024+ → desktop

/** Pre-built media query strings (avoids numeric template interpolation) */
const MOBILE_QUERY = "(max-width: 767px)";
const TABLET_QUERY = "(min-width: 768px) and (max-width: 1023px)";

type Breakpoint = "mobile" | "tablet" | "desktop";

/**
 * Returns `true` when the viewport is ≤ 767 px (mobile).
 *
 * Uses `matchMedia` so the value updates live on resize.
 * Defaults to `false` (desktop) during SSR / first server render.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  });

  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);

    const handler = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };

    // Sync in case the value changed between render and effect
    setIsMobile(mql.matches);

    mql.addEventListener("change", handler);
    return () => {
      mql.removeEventListener("change", handler);
    };
  }, []);

  return isMobile;
}

/**
 * Returns the current breakpoint name: `'mobile'`, `'tablet'`, or `'desktop'`.
 *
 * Breakpoints:
 * - mobile  → 0 – 767 px
 * - tablet  → 768 – 1023 px
 * - desktop → 1024 px +
 *
 * Defaults to `'desktop'` during SSR.
 */
export function useBreakpoint(): Breakpoint {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(() => {
    if (typeof window === "undefined") return "desktop";
    return resolveBreakpoint(window.innerWidth);
  });

  useEffect(() => {
    const mobileQuery = window.matchMedia(MOBILE_QUERY);
    const tabletQuery = window.matchMedia(TABLET_QUERY);

    const update = () => {
      if (mobileQuery.matches) {
        setBreakpoint("mobile");
      } else if (tabletQuery.matches) {
        setBreakpoint("tablet");
      } else {
        setBreakpoint("desktop");
      }
    };

    // Sync on mount
    update();

    mobileQuery.addEventListener("change", update);
    tabletQuery.addEventListener("change", update);

    return () => {
      mobileQuery.removeEventListener("change", update);
      tabletQuery.removeEventListener("change", update);
    };
  }, []);

  return breakpoint;
}

/* ── helpers ─────────────────────────────────────────── */

function resolveBreakpoint(width: number): Breakpoint {
  if (width <= MOBILE_MAX) return "mobile";
  if (width <= TABLET_MAX) return "tablet";
  return "desktop";
}

export type { Breakpoint };
